import React from 'react';
import {
  Box,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { useHistory } from 'react-router';
import { PlayArrow, Redo, Undo } from '@material-ui/icons';
import { FaEye } from 'react-icons/fa6';

import { entityBaseBodyRender } from '@app/components/common/table/body-renders/entity-base';
import { dateTimeBodyRender } from '@app/components/common/table/body-renders/date-time';
import GalleryModal from '@app/components/common/modals/gallery';
import { booleanBodyRender } from '@app/components/common/table/body-renders/bool';
import BoolCell from '@app/components/common/table/cells/bool';
import { userBodyRender } from '@app/components/common/table/body-renders/user';
import { Address } from '@app/api/models/address';
import { TableColumn } from '@app/components/common/table';
import { Txt } from '@app/components/common';
import { NewOutlet } from '@app/api/models';
import { sortEntityByName } from '@app/utils';

import { ImageDescriptor } from '../../../api/models/image';
import DetailViewModal from './detail-modal';
import MapViewModal from './map-modal';
import FrequencyVisitsModal from './frequency-visits-modal';

export const useColumns = (canEdit: boolean, onUncancelClick: (id: number) => void): TableColumn<NewOutlet>[] => {
  const history = useHistory();
  return [
    {
      name: 'id',
      label: '#',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'name',
      label: 'Наименование',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (name: string, { rowData }) => (
          <>
            <Txt color="brand.500" style={{ fontSize: 16 }}>
              {name}
            </Txt>
            <Box display="flex">
              <Txt color="gray.500">Вывеска:&nbsp;</Txt>
              <Txt>{rowData.title}</Txt>
            </Box>
            <Box display="flex">
              <Txt color="gray.500">Подсеть:&nbsp;</Txt>
              <Txt>{rowData.subnet}</Txt>
            </Box>
          </>
        ),
      },
    },
    {
      name: 'division',
      label: 'Фед.округ',
      options: {
        filter: {
          name: 'division_id',
          sourceName: 'division',
          type: 'multi-select',
          //onApply: (value: string[]) => value.map((i) => Number(i)),
        },
        sort: true,
        customBodyRender: entityBaseBodyRender(),
      },
    },
    {
      name: 'channel',
      label: 'Подканал',
      options: {
        filter: {
          name: 'channel_id',
          sourceName: 'outlet_sub_channel',
          type: 'multi-select',
          onOptionsFetch: (channels: EntityBase[]) => sortEntityByName(
            channels.map(({ id, name, channel }) => {
              return { id, name: `${channel}: ${name}`};
            })
          )
        },
        sort: true,
        customBodyRender: (value, { rowData }) => {
          if (!value) {return <Txt  color='gray.500'>[Пусто]</Txt>;}
          return <Flex gap={2}>
            <Txt color='gray.500'>{value?.name}:</Txt>
            <Txt color='brand.500'>{rowData.sub_channel?.name}</Txt>
          </Flex>;
        },
      },
    },
    {
      name: 'route_number',
      label: 'Номер маршрута',
      options: {
        filter: {
          name: 'route_number_id',
          sourceName: 'route_number',
          type: 'autocomplete-paginated',
        },
        sort: false,
        customBodyRender: entityBaseBodyRender(false),
      },
    },
    {
      name: 'address',
      label: 'Адрес',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (address: Address, { rowData }) => (
          <>
            <Txt>{address.address}</Txt>
            <MapViewModal
              title={rowData.name}
              coords={rowData.latlng}
            />
          </>
        ),
      },
    },
    {
      name: 'payment_type',
      label: 'Способ оплаты',
      options: {
        filter: {
          name: 'payment_type_id',
          sourceName: 'payment_type',
          type: 'multi-select',
        },
        sort: true,
        customBodyRender: entityBaseBodyRender(),
      },
    },
    {
      name: 'inn',
      label: 'Реквизиты',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (_, { rowData }) => (
          <>
            <DetailViewModal outlet={rowData} />
          </>
        ),
      },
    },
    {
      name: 'visits_day',
      label: 'Частота посещений в мес.',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <>
            <FrequencyVisitsModal value={value} />
          </>
        ),
      },
    },
    {
      name: 'director',
      label: 'Директор',
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: 'phone',
      label: 'Контакты',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (phone, { rowData }) => (
          <Box display="flex" flexDirection="column">
            <Txt>{phone}</Txt>
            <Txt>{rowData.email}</Txt>
          </Box>
        ),
      },
    },
    {
      name: 'create_dt',
      label: 'Дата создания',
      options: {
        filter: {
          name: 'create_date_range',
          type: 'date-range',
          onApply: (value) => {
            return value.map((date) => date.split('T')[0]);
          }
        },
        sort: true,
        customBodyRender: dateTimeBodyRender(false, false),
      },
    },
    {
      name: 'acb_user',
      label: 'Пользователь',
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value: { full_name: string, username: string }) => {
          return value ? `${value?.full_name} [${value.username}]` : '';
        },
      },
    },
    {
      name: 'images',
      label: '',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: Array<ImageDescriptor> | undefined) => {
          return <GalleryModal images={value || []} title="Фото" />;
        },
      },
    },
    {
      name: 'acb_user',
      label: 'Торг. представитель',
      options: {
        filter: true,
        sort: false,
        customBodyRender: userBodyRender(),
      }
    },
    {
      name: 'status',
      label: 'Статус',
      options: {
        filter: {
          label: 'Статус',
          name: 'status_id',
          type: 'multi-select',
          sourceName: 'outlet_acb_status',
        },
        sort: false,
        customBodyRender: entityBaseBodyRender(false),
      },
    },
    {
      name: 'comment',
      label: 'Комментарий',
      options: {
        filter: false,
        sort: true,
      },
    },
    {
      name: 'mastered_dt',
      label: 'Дата добавления',
      options: {
        filter: false,
        sort: true,
        customBodyRender: dateTimeBodyRender(false, false),
      },
    },
    {
      name: 'status',
      label: 'Действия',
      options: {
        hidden: !canEdit,
        filter: false,
        sort: false,
        customBodyRender: (status, { rowData }) => {
          if (!canEdit) {
            return null;
          }
          return (
            <Flex gap={2} alignItems='center'>
              {(status.id === 3 || status.id === 2) && <IconButton
                onClick={() => {
                  history.push(`/acl/new-outlets/${rowData.id}/view`, {
                    outlet: rowData,
                  });
                } } aria-label="edit"
              >
                <FaEye />
              </IconButton>}
              {status.id === 3 && <IconButton
                onClick={() => {
                  onUncancelClick(Number(rowData.id));
                } } aria-label="edit"
              >
                <Undo />
              </IconButton>}
              {status.id === 1 && (
                <IconButton
                  onClick={() => {
                    history.push('/acl/outlets/create/', {
                      outlet: rowData,
                    });
                  } } aria-label="create"
                >
                  <PlayArrow />
                </IconButton>
              )}
            </Flex>
          );
        },
      },
    },
    {
      name: 'email',
      label: 'Email',
      options: {
        hidden: true,
        filter: false,
      },
    },
    {
      name: 'latlng',
      label: 'latlng',
      options: {
        hidden: true,
        filter: false,
      },
    },
  ];
};
